import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { periksaEmailBaru, rapikanEmail } from "@/lib/akunAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// Ubah email akun admin (perbaikan salah ketik) -- khusus Super Admin (PUT /users/{id}/email).
// Pengaman salah ketik: email baru diketik dua kali, lalu konfirmasi "dari <lama> menjadi <baru>".
// Hanya email yg berubah; kata sandi, peran, status, cakupan, & authenticator tetap.
export default function UbahEmailAkunDialog({ target, onClose, onDone }) {
    const [baru, setBaru] = useState("");
    const [konfirmasi, setKonfirmasi] = useState("");
    const [galat, setGalat] = useState(null);
    const [yakin, setYakin] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setBaru(""); setKonfirmasi(""); setGalat(null); setYakin(false);
    }, [target?.id]);

    const lanjut = (e) => {
        e?.preventDefault();
        const pesan = periksaEmailBaru(target.email, baru, konfirmasi);
        setGalat(pesan);
        if (!pesan) setYakin(true);
    };

    const simpan = async () => {
        setSaving(true);
        try {
            await api.put(`/users/${target.id}/email`, { email_baru: baru, konfirmasi_email: konfirmasi });
            toast.success("Email akun diperbarui. Pemilik akun login dengan email baru dan kata sandi yang sama.");
            setYakin(false);
            onDone();
        } catch (e) {
            const pesan = formatApiErrorDetail(e.response?.data?.detail);
            setYakin(false);
            setGalat(pesan);
            toast.error(pesan);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Dialog open={!!target && !yakin} onOpenChange={(o) => !o && !yakin && onClose()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Ubah Email Akun</DialogTitle>
                        <DialogDescription>
                            Untuk memperbaiki salah ketik. Kata sandi, peran, status, dan cakupan akun tidak berubah.
                            Semua sesi akun ini akan keluar dan pemiliknya login ulang dengan email baru.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={lanjut} className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Email sekarang</Label>
                            <div className="font-mono text-sm text-slate-800 break-all" data-testid="ubah-email-lama">{target?.email}</div>
                        </div>
                        <div>
                            <Label htmlFor="ubah-email-baru" className="text-xs font-semibold uppercase text-slate-600">Email baru</Label>
                            <Input
                                id="ubah-email-baru" type="email" autoComplete="off" maxLength={254} value={baru}
                                onChange={(e) => { setBaru(e.target.value); setGalat(null); }} data-testid="ubah-email-baru"
                            />
                        </div>
                        <div>
                            <Label htmlFor="ubah-email-konfirmasi" className="text-xs font-semibold uppercase text-slate-600">
                                Ketik ulang email baru
                            </Label>
                            <Input
                                id="ubah-email-konfirmasi" type="email" autoComplete="off" maxLength={254} value={konfirmasi}
                                onChange={(e) => { setKonfirmasi(e.target.value); setGalat(null); }}
                                onPaste={(e) => e.preventDefault()} data-testid="ubah-email-konfirmasi"
                            />
                            <p className="text-[11px] text-slate-500 mt-1">Ketik ulang (tidak bisa ditempel) agar salah ketik terdeteksi.</p>
                        </div>
                        {galat && <p className="text-sm text-rose-700" role="alert" data-testid="ubah-email-galat">{galat}</p>}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
                            <Button type="submit" className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="ubah-email-lanjut">Lanjut</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!target && yakin} onOpenChange={(o) => !o && setYakin(false)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Ubah email akun ini?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm">
                                <div>
                                    Dari <b className="font-mono break-all">{target?.email}</b> menjadi{" "}
                                    <b className="font-mono break-all" data-testid="ubah-email-konfirmasi-baru">{rapikanEmail(baru)}</b>
                                </div>
                                <div>Email lama tidak bisa dipakai login lagi. Semua sesi akun ini akan keluar.</div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={saving}>Periksa lagi</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={saving} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="ubah-email-simpan"
                            onClick={(e) => { e.preventDefault(); simpan(); }}
                        >
                            {saving ? "Menyimpan..." : "Ya, ubah email"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
